import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { AnimatePresence } from 'framer-motion';
import { NEON_BLUE } from './styles/theme';
import { keyframes } from '@emotion/react';
import { MainPage } from './pages/MainPage';
import { trackPageView } from './utils/analytics';

// Import pages
import MusicPage from './pages/MusicPage';
import IOSAppsPage from './pages/IOSAppsPage';
import WebAppsPage from './pages/WebAppsPage';
import CrypticPage from './pages/CrypticPage';
import TsukikagePage from './pages/TsukikagePage';
import VideosPage from './pages/VideosPage';
import NexusPage from './pages/NexusPage';
import TransmissionPage from './pages/TransmissionPage';
import DiscordPage from './pages/DiscordPage';
import ResearchPage from './pages/ResearchPage';
import PortfolioPage from './pages/PortfolioPage';
import BuildPage from './pages/BuildPage';
import MerchPage from './pages/MerchPage';

// Add new background animations
const gridAnimation = keyframes`
  0% {
    background-position: 0 0;
    opacity: 0.8;
  }
  100% {
    background-position: 50px 50px;
    opacity: 0.3;
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
    opacity: 0.3;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.7;
  }
`;

const scanlineAnimation = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
`;

// Update the BackgroundAnimation component
const BackgroundAnimation = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  background: 
    linear-gradient(to bottom, #000000, #001414);
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(90deg, ${NEON_BLUE}22 1px, transparent 1px) 0 0 / 50px 50px,
      linear-gradient(${NEON_BLUE}22 1px, transparent 1px) 0 0 / 50px 50px;
    animation: ${gridAnimation} 20s linear infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      transparent 0%,
      ${NEON_BLUE}11 10%,
      ${NEON_BLUE}22 50%,
      ${NEON_BLUE}11 90%,
      transparent 100%
    );
    animation: ${scanlineAnimation} 10s linear infinite;
    opacity: 0.5;
  }
`;

const BackgroundGlow = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  width: 600px;
  height: 600px;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle,
    ${NEON_BLUE}22 0%,
    ${NEON_BLUE}11 30%,
    transparent 70%
  );
  animation: ${pulseAnimation} 5s ease-in-out infinite;
  z-index: -1;
`;

// Add AppContext for state management
const AppContext = React.createContext<{
  stats: {
    health: number;
    energy: number;
    bootPhase: number;
  };
}>({
  stats: {
    health: 100,
    energy: 100,
    bootPhase: 0
  }
});

// Analytics tracker component
const AnalyticsTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Get page name from pathname
    const pageName = location.pathname.split('/').pop() || 'home';
    trackPageView(pageName);
  }, [location]);
  
  return null;
};

// Update the AppProvider component
const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stats] = useState({
    health: 100,
    energy: 100,
    bootPhase: 0
  });
  
  return (
    <AppContext.Provider value={{ stats }}>
      <Router>
        <AnalyticsTracker />
        {children}
      </Router>
    </AppContext.Provider>
  );
};

function App() {
  return (
    <AppProvider>
      <BackgroundAnimation />
      <BackgroundGlow />
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={<MainPage />} />
          <Route path="/music" element={<MusicPage />} />
          <Route path="/transmission" element={<TransmissionPage />} />
          <Route path="/ios-apps" element={<IOSAppsPage />} />
          <Route path="/web-apps" element={<WebAppsPage />} />
          <Route path="/discord" element={<DiscordPage />} />
          <Route path="/cryptic" element={<CrypticPage />} />
          <Route path="/hidden-transmission" element={<CrypticPage />} />
          <Route path="/tsukikage" element={<TsukikagePage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/nexus" element={<NexusPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/portfolio" element={<PortfolioPage />} />
          <Route path="/build" element={<BuildPage />} />
          <Route path="/merch" element={<MerchPage />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AnimatePresence>
    </AppProvider>
  );
}

export default App;
